import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import proxy from "api/proxy";
import funcs from "tools/functions";
import { genStoreId } from "componentsv2/tools/functions";
import { useTable } from "storev2/tableStore";
import { useWizard } from "storev2/wizardStore";
import Button from "componentsv2/button/Button";
import { cn } from "tools/ui";

const terminal = funcs.getCookie();

const ALL_TURNS = "ALL";
const model = "account.statement.sale";

function getDefaults() {
  const { getTable } = useTable.getState();

  const storeId = genStoreId("account.statement", null);
  const statements = getTable(storeId);
  const record = statements?.records?.[0];

  return {
    statementDate: record?.date ?? null,
    user: record?.["create_uid."]?.name ?? null,
    defaultTurn: record?.turn ?? null,
  };
}

function ViewItem({ label, value }) {
  const _label = `${model}.${label}`;

  return (
    <div className="flex flex-col py-2 px-4 rounded-2xl bg-white border border-neutral-300 w-full">
      <span className="text-lg text-neutral-700">
        <FM id={_label} />
      </span>
      <span className="text-3xl">
        {new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 0,
        }).format(value || 0)}
      </span>
    </div>
  );
}

function View() {
  const { store } = useWizard();
  const [currentTurn, setCurrentTurn] = useState(null);
  const [totals, setTotals] = useState({ delivery: 0, tip: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [hasSales, setHasSales] = useState(false);

  const { statementDate, user, defaultTurn } = store;

  async function fetchData(turnValue) {
    if (!statementDate || !terminal?.device_id) return;

    setLoading(true);

    const fields = ["delivery_amount", "tip_amount", "sale_device", "quoted_by"];

    const dom = [
      ["sale_device", "=", Number(terminal.device_id)],
      ["sale_date", ">=", statementDate],
      ["OR", ["state", "=", "processing"], ["state", "=", "done"]],
    ];

    if (turnValue === null) {
      if (defaultTurn !== null) {
        dom.push(["turn", "=", defaultTurn]);
      }
    } else if (turnValue !== ALL_TURNS) {
      dom.push(["turn", "=", turnValue]);
    }

    const { data } = await proxy.search("sale.sale", dom, fields);

    const totalTips = (data || []).reduce(
      (acc, item) => acc + (item.tip_amount || 0),
      0,
    );

    const totalDelivery = (data || []).reduce(
      (acc, item) => acc + (item.delivery_amount || 0),
      0,
    );

    setHasSales((data || []).length > 0);
    setTotals({
      total: totalDelivery + totalTips,
      delivery: totalDelivery,
      tip: totalTips,
    });
    setLoading(false);
  }

  useEffect(() => {
    fetchData(currentTurn);
  }, [currentTurn, statementDate]);

  const activeTurn =
    currentTurn === null ? String(defaultTurn) : currentTurn;

  const handleTurn = async (turn) => {
    setCurrentTurn(turn);
  };

  if (!terminal?.device_id || !statementDate) {
    return <p>No hay estados de cuenta abiertos</p>;
  }

  if (loading) {
    return <p>Cargando...</p>;
  }

  if (!hasSales) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="font-bold mb-1">Datos de estado de cuenta actual</h2>
          <div className="space-y-2 text-2xl">
            <div>
              <span className="font-bold">Turno: </span>
              <span>{defaultTurn ?? "Sin turno"}</span>
            </div>
            <div>
              <span className="font-bold">Fecha: </span>
              <span>{statementDate}</span>
            </div>
            <div>
              <span className="font-bold">Creado por: </span>
              <span>{user}</span>
            </div>
          </div>
        </div>
        <hr className="border-b border-neutral-300" />
        <p>No hay ventas en este turno</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-bold mb-1">Datos de estado de cuenta actual</h2>
        <div className="space-y-2 text-2xl">
          <div>
            <span className="font-bold">Turno: </span>
            <span>{defaultTurn ?? "Sin turno"}</span>
          </div>
          <div>
            <span className="font-bold">Fecha: </span>
            <span>{statementDate}</span>
          </div>
          <div>
            <span className="font-bold">Creado por: </span>
            <span>{user}</span>
          </div>
        </div>
      </div>

      <hr className="border-b border-neutral-300" />

      <div>
        <h2 className="font-bold mb-1">Selecciona tu turno</h2>
        <div className="flex space-x-3">
          {[ALL_TURNS, "1", "2", "3"].map((turn) => (
            <Button
              key={turn}
              onClick={() => handleTurn(turn)}
              style={cn(
                "px-4 py-1 rounded-lg border text-sm font-medium transition !w-32 !md:w-40",
                activeTurn === turn
                  ? "bg-amber-300 border-amber-300 text-gray-900"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100",
              )}
            >
              {turn === ALL_TURNS ? "Todos" : `Turno ${turn}`}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 mx-auto justify-center gap-8">
        <ViewItem label="delivery" value={totals.delivery} />
        <ViewItem label="tips" value={totals.tip} />
        <ViewItem label="total" value={totals.total} />
      </div>
    </div>
  );
}

const viewTips = {
  start: {
    label: "close_start",
    Component: View,
    initialStore: getDefaults,
  },
};

export { viewTips };
